import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { CalendarClock, Loader2, UserX, CalendarPlus, BadgeCheck, CheckCircle2, FileSearch, RefreshCw } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";

const fmt = (iso) => (iso ? iso.split("-").reverse().join("/") : "—");

export const ExpiredContractsWidget = () => {
    const [items, setItems] = useState(null);
    const [action, setAction] = useState(null); // {emp, type}
    const [followUp, setFollowUp] = useState(null); // {emp, type} — remind to upload the UNILAV
    const [date, setDate] = useState("");
    const [busy, setBusy] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [selected, setSelected] = useState([]);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkBusy, setBulkBusy] = useState(false);
    const navigate = useNavigate();

    const toggleSelect = (id) =>
        setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

    const runBulkCessa = async () => {
        setBulkBusy(true);
        try {
            const { data } = await api.post("/employees/contract-actions/bulk-cessa", {
                employee_ids: selected,
            });
            toast.success(`${data.ceased} dipendent${data.ceased === 1 ? "e cessato" : "i cessati"} alla scadenza naturale del contratto`);
            if (data.unilav_reminders > 0) {
                toast.info(`${data.unilav_reminders} promemoria UNILAV di cessazione creati (contratti non a termine)`);
            } else {
                toast.info("Nessun UNILAV di cessazione da inviare: fa fede il termine dell'assunzione", { duration: 7000 });
            }
            setSelected([]);
            setBulkOpen(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore cessazione multipla");
        } finally {
            setBulkBusy(false);
        }
    };

    const syncFromArchive = async () => {
        setSyncing(true);
        try {
            const { data } = await api.post("/unilav/sync-archive", {
                employee_ids: items.map((i) => i.id),
                only_unchecked: false,
            });
            if (!data.job_id) {
                toast.info("Nessun UNILAV in archivio da analizzare per questi dipendenti");
                setSyncing(false);
                return;
            }
            toast.info(`Analisi AI avviata su ${data.total} UNILAV in archivio…`);
            const poll = setInterval(async () => {
                try {
                    const { data: job } = await api.get(`/unilav/sync-jobs/${data.job_id}`);
                    if (job.status === "done") {
                        clearInterval(poll);
                        setSyncing(false);
                        toast.success(`Analisi completata: ${job.applied} contratt${job.applied === 1 ? "o aggiornato" : "i aggiornati"}`);
                        load();
                    }
                } catch (e) {
                    clearInterval(poll);
                    setSyncing(false);
                }
            }, 5000);
        } catch (e) {
            toast.error("Errore avvio analisi");
            setSyncing(false);
        }
    };

    const load = async () => {
        try {
            const { data } = await api.get("/employees/expired-contracts");
            setItems(data.items);
        } catch (e) {
            setItems([]);
        }
    };
    useEffect(() => { load(); }, []);

    if ((!items || items.length === 0) && !followUp) return null;

    const openAction = (emp, type) => {
        setAction({ emp, type });
        setDate(type === "cessa" ? emp.contratto_scadenza || "" : "");
    };

    const run = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/employees/${action.emp.id}/contract-action`, {
                action: action.type,
                date: date || null,
            });
            const labels = { cessa: "cessato", proroga: "prorogato", trasforma: "trasformato a indeterminato" };
            toast.success(`${action.emp.cognome} ${action.emp.nome}: contratto ${labels[action.type]}`);
            if (action.type === "cessa" && data.unilav_required === false) {
                toast.info("Contratto a termine cessato a scadenza naturale: nessun UNILAV di cessazione da inviare (fa fede il termine dell'assunzione)", { duration: 8000 });
            } else {
                setFollowUp({ emp: action.emp, type: action.type });
            }
            setAction(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore");
        } finally {
            setBusy(false);
        }
    };

    const fuLabels = { cessa: "Cessazione", proroga: "Proroga", trasforma: "Trasformazione" };

    return (
        <>
        {items && items.length > 0 && (
        <section className="rounded-lg border border-orange-200 bg-orange-50/50 p-5 shadow-sm" data-testid="expired-contracts-widget">
            <h2 className="mb-1 flex items-center justify-between gap-2 font-display text-base font-semibold text-orange-800">
                <span className="flex items-center gap-2">
                    <CalendarClock className="h-4 w-4" />
                    Contratti scaduti da gestire ({items.length})
                </span>
                <Button
                    size="sm"
                    variant="ghost"
                    onClick={syncFromArchive}
                    disabled={syncing}
                    className="h-7 gap-1.5 px-2 text-[11px] font-semibold text-orange-700 hover:bg-orange-100"
                    title="Analizza con l'AI gli UNILAV in archivio di questi dipendenti: se trova proroghe o trasformazioni più recenti aggiorna i contratti"
                    data-testid="sync-unilav-archive-btn"
                >
                    {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                    Sincronizza da archivio
                </Button>
            </h2>
            <p className="mb-3 text-xs text-orange-700/80">
                Dipendenti attivi con contratto scaduto: decidi tu se cessare, prorogare o segnare la trasformazione a indeterminato.
            </p>
            <div className="mb-2 flex flex-wrap items-center gap-3">
                <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs font-medium text-orange-800">
                    <input
                        type="checkbox"
                        checked={selected.length === items.length && items.length > 0}
                        onChange={(e) => setSelected(e.target.checked ? items.map((i) => i.id) : [])}
                        className="h-3.5 w-3.5 rounded border-orange-300"
                        data-testid="select-all-expired"
                    />
                    Seleziona tutti
                </label>
                {selected.length > 0 && (
                    <Button
                        size="sm"
                        onClick={() => setBulkOpen(true)}
                        className="h-7 gap-1.5 bg-red-600 px-2.5 text-[11px] text-white hover:bg-red-700"
                        data-testid="bulk-cessa-btn"
                    >
                        <UserX className="h-3 w-3" />
                        Cessa selezionati ({selected.length})
                    </Button>
                )}
            </div>
            <div className="max-h-64 space-y-1.5 overflow-y-auto">
                {items.map((e) => (
                    <div key={e.id} className="flex flex-wrap items-center gap-2 rounded-md border border-orange-100 bg-white px-3 py-2" data-testid={`expired-contract-row-${e.matricola}`}>
                        <input
                            type="checkbox"
                            checked={selected.includes(e.id)}
                            onChange={() => toggleSelect(e.id)}
                            className="h-3.5 w-3.5 flex-shrink-0 rounded border-slate-300"
                            data-testid={`select-emp-${e.matricola}`}
                        />
                        <div className="min-w-0 flex-1">
                            <Link to={`/dipendenti/${e.id}`} className="truncate text-sm font-semibold text-slate-700 hover:underline">
                                {e.cognome} {e.nome}
                            </Link>
                            <div className="text-[11px] text-slate-500">
                                matr. {e.matricola} · {e.contratto_tipo || "?"} · <span className="font-semibold text-red-600">scaduto il {fmt(e.contratto_scadenza)}</span>
                            </div>
                        </div>
                        <div className="flex gap-1.5">
                            <Button size="sm" variant="outline" onClick={() => openAction(e, "cessa")} className="h-7 gap-1 border-red-200 bg-red-50 px-2 text-[11px] text-red-700 hover:bg-red-100" data-testid={`cessa-btn-${e.matricola}`}>
                                <UserX className="h-3 w-3" /> Cessa
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => openAction(e, "proroga")} className="h-7 gap-1 border-blue-200 bg-blue-50 px-2 text-[11px] text-blue-700 hover:bg-blue-100" data-testid={`proroga-btn-${e.matricola}`}>
                                <CalendarPlus className="h-3 w-3" /> Proroga
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => openAction(e, "trasforma")} className="h-7 gap-1 border-emerald-200 bg-emerald-50 px-2 text-[11px] text-emerald-700 hover:bg-emerald-100" data-testid={`trasforma-btn-${e.matricola}`}>
                                <BadgeCheck className="h-3 w-3" /> Indeterminato
                            </Button>
                        </div>
                    </div>
                ))}
            </div>
        </section>
        )}

            <Dialog open={!!action} onOpenChange={(o) => !o && setAction(null)}>
                <DialogContent className="max-w-sm" data-testid="contract-action-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-slate-800">
                            {action?.type === "cessa" && "Cessa dipendente"}
                            {action?.type === "proroga" && "Proroga contratto"}
                            {action?.type === "trasforma" && "Trasformazione a indeterminato"}
                        </DialogTitle>
                        <DialogDescription>
                            {action?.emp.cognome} {action?.emp.nome} (matr. {action?.emp.matricola}) — {action?.emp.contratto_tipo}
                        </DialogDescription>
                    </DialogHeader>
                    {action?.type === "cessa" && (
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Data di cessazione</label>
                            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 h-9 w-full rounded-md border border-slate-200 px-2 text-sm" data-testid="contract-action-date" />
                            <p className="mt-2 text-xs text-slate-500">Il dipendente verrà segnato come cessato con motivo "Scadenza contratto".</p>
                            {/determinato/i.test(action?.emp.contratto_tipo || "") && !/indeterminato/i.test(action?.emp.contratto_tipo || "") && date === action?.emp.contratto_scadenza && (
                                <p className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-xs text-emerald-700" data-testid="no-unilav-hint">
                                    Cessazione a scadenza naturale del contratto a termine: <b>nessun UNILAV di cessazione da inviare</b> (fa fede il termine indicato nell'assunzione).
                                </p>
                            )}
                        </div>
                    )}
                    {action?.type === "proroga" && (
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Nuova scadenza contratto</label>
                            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 h-9 w-full rounded-md border border-slate-200 px-2 text-sm" data-testid="contract-action-date" />
                        </div>
                    )}
                    {action?.type === "trasforma" && (
                        <p className="text-sm text-slate-600">
                            Il tipo contratto diventerà <b>Indeterminato</b> e la data di scadenza verrà rimossa.
                        </p>
                    )}
                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" onClick={() => setAction(null)}>Annulla</Button>
                        <Button
                            onClick={run}
                            disabled={busy || (action?.type !== "trasforma" && !date)}
                            className={`gap-2 text-white ${action?.type === "cessa" ? "bg-red-600 hover:bg-red-700" : "bg-[#376DB3] hover:bg-[#2c5a96]"}`}
                            data-testid="contract-action-confirm"
                        >
                            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                            Conferma
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={bulkOpen} onOpenChange={(o) => !o && setBulkOpen(false)}>
                <DialogContent className="max-w-md" data-testid="bulk-cessa-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-slate-800">Cessazione multipla ({selected.length})</DialogTitle>
                        <DialogDescription>
                            Ogni dipendente verrà cessato alla <b>propria data di scadenza contrattuale</b> con motivo "Scadenza contratto".
                        </DialogDescription>
                    </DialogHeader>
                    <ul className="max-h-52 space-y-1 overflow-y-auto text-sm">
                        {(items || []).filter((e) => selected.includes(e.id)).map((e) => (
                            <li key={e.id} className="flex items-center justify-between rounded border border-slate-100 bg-slate-50 px-2.5 py-1.5">
                                <span className="font-medium text-slate-700">{e.cognome} {e.nome} <span className="text-xs font-normal text-slate-500">matr. {e.matricola}</span></span>
                                <span className="text-xs text-red-600">cessa il {fmt(e.contratto_scadenza)}</span>
                            </li>
                        ))}
                    </ul>
                    <p className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-xs text-emerald-700">
                        Per i contratti a termine cessati a scadenza naturale <b>non serve inviare l'UNILAV di cessazione</b>.
                    </p>
                    <div className="flex justify-end gap-2 pt-1">
                        <Button variant="outline" onClick={() => setBulkOpen(false)}>Annulla</Button>
                        <Button
                            onClick={runBulkCessa}
                            disabled={bulkBusy || selected.length === 0}
                            className="gap-2 bg-red-600 text-white hover:bg-red-700"
                            data-testid="bulk-cessa-confirm"
                        >
                            {bulkBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                            Cessa {selected.length} dipendenti
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={!!followUp} onOpenChange={(o) => !o && setFollowUp(null)}>
                <DialogContent className="max-w-sm" data-testid="unilav-reminder-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 font-display text-slate-800">
                            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                            {fuLabels[followUp?.type]} registrata
                        </DialogTitle>
                        <DialogDescription>
                            {followUp?.emp.cognome} {followUp?.emp.nome} (matr. {followUp?.emp.matricola})
                        </DialogDescription>
                    </DialogHeader>
                    <p className="text-sm text-slate-600">
                        Per tenere allineato l'archivio, carica ora la <b>ricevuta UNILAV di {fuLabels[followUp?.type]?.toLowerCase()}</b>.
                        Ho creato un promemoria: lo trovi nella pagina <b>Scadenze</b> finché non carichi il documento.
                    </p>
                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" onClick={() => setFollowUp(null)} data-testid="unilav-reminder-later">
                            Più tardi
                        </Button>
                        <Button
                            onClick={() => { setFollowUp(null); navigate("/unilav"); }}
                            className="gap-2 bg-[#376DB3] text-white hover:bg-[#2c5a96]"
                            data-testid="unilav-reminder-upload-btn"
                        >
                            <FileSearch className="h-4 w-4" />
                            Carica UNILAV ora
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};
